/**
 * FormatPatterns.js
 *
 * Registry of format patterns for AMFI tags and system actions.
 * Used by the typeahead system to provide format guidance during user input.
 */

// Central registry of all format patterns supported by the system
export const formatPatterns = {
	// All actions that can be triggered with # including AMFI tags
	actions: {
		// AMFI Tag Actions
		attribute: {
			trigger: '#attribute',
			sequence: [
				{ name: 'name', description: 'Attribute name' },
				{ name: 'value', description: 'Attribute value' },
			],
		},
		metric: {
			trigger: '#metric',
			sequence: [
				{ name: 'name', description: 'Metric name' },
				{ name: 'value', description: 'Numeric value' },
				{
					name: 'unit',
					description: 'Unit of measurement (optional)',
					optional: true,
				},
			],
		},
		fact: {
			trigger: '#fact',
			sequence: [
				{ name: 'category', description: 'Fact category' },
				{ name: 'statement', description: 'Fact statement' },
			],
		},
		insight: {
			trigger: '#insight',
			sequence: [
				{ name: 'type', description: 'Insight type' },
				{ name: 'statement', description: 'Insight statement' },
			],
		},

		// System Actions
		research: {
			trigger: '#research',
			sequence: [{ name: 'topic', description: 'Research topic' }],
		},
		todo: {
			trigger: '#todo',
			sequence: [
				{ name: 'task_description', description: 'Task description' },
				{
					name: 'deadline',
					description: 'Deadline (optional)',
					optional: true,
				},
			],
		},
		reminder: {
			trigger: '#reminder',
			sequence: [
				{ name: 'date', description: 'Reminder date' },
				{ name: 'description', description: 'Reminder description' },
			],
		},
	},
};

/**
 * Analyzes input text to determine what format pattern is being used
 * and where in the pattern sequence the user currently is.
 *
 * @param {string} text - The input text to analyze
 * @param {number} cursorPosition - Current cursor position in the text
 * @returns {Object|null} Format analysis result or null if no pattern detected
 */
export const analyzeFormat = (text, cursorPosition) => {
	// Extract the text before the cursor to analyze
	let textBeforeCursor = text.substring(0, cursorPosition);

	// If the text contains a newline, check only from the last newline
	let lastNewlineIndex = textBeforeCursor.lastIndexOf('\n');
	if (lastNewlineIndex !== -1) {
		textBeforeCursor = textBeforeCursor.substring(lastNewlineIndex + 1);
	}

	// If there's no # in the current line, return null immediately
	if (!textBeforeCursor.includes('#')) {
		return null;
	}

	// Immediately check for exact action matches without requiring a colon
	for (const [actionType, pattern] of Object.entries(formatPatterns.actions)) {
		// Action with no colon yet - base case (e.g., "#attribute" or "#attribute ")
		const baseActionRegex = new RegExp(`#${actionType}(?:\\s*$|\\s+[^:])`);
		if (baseActionRegex.test(textBeforeCursor)) {
			return {
				type: 'action',
				actionType: actionType,
				sequenceIndex: 0,
				hint: `:${pattern.sequence[0].name}`,
			};
		}

		// Check for the pattern with segments separated by colons
		const fullPattern = textBeforeCursor.substring(
			textBeforeCursor.lastIndexOf('#')
		);

		// Split by colon, but preserve the text structure
		const segments = fullPattern.split(':');

		// First segment should be the action type (without the #)
		if (segments[0].substring(1).trim() === actionType) {
			const segmentCount = segments.length - 1; // -1 because first segment is action type

			// If we're within the valid segment range
			if (segmentCount <= pattern.sequence.length) {
				// If we're at the beginning of a new segment (just added a colon)
				if (textBeforeCursor.endsWith(':')) {
					return {
						type: 'action',
						actionType: actionType,
						sequenceIndex: segmentCount,
						hint: pattern.sequence[segmentCount - 1].name, // Show the name of the current segment
					};
				}
				// Otherwise show the hint for the next segment
				else {
					return {
						type: 'action',
						actionType: actionType,
						sequenceIndex: segmentCount,
						hint:
							segmentCount < pattern.sequence.length
								? `:${pattern.sequence[segmentCount].name}${pattern.sequence[segmentCount].optional ? ' (optional)' : ''}`
								: '',
					};
				}
			}
		}
	}

	// If no pattern detected, return null
	return null;
};
